// A record's Path: the steps of one picklist field shown across the top of its
// page, the last step Closed with two outcomes. An opportunity's stage and a
// lead's status. Pure, so it tests; ObjectRecordView draws it and confirms a
// click before saving. Each step reached shows when and by whom, from the
// record's path_history (from ops.field_history).
import type { ObjectKey } from '../navigation';
import { formatShortDate } from './format';

export interface PathOutcome {
  /** The field's value for this outcome. */
  value: string;
  label: string;
  /** How the library's Path colours it. */
  outcome: 'won' | 'lost';
  /**
   * False for an outcome the path shows but does not offer when closing (a
   * quote's Expired): it is set from the field itself.
   */
  offered?: boolean;
}

export interface PathDef {
  /** The picklist field the path sets. */
  field: string;
  /** "stage", "status": the word in the confirm dialog. */
  noun: string;
  /** The open steps, in order. */
  steps: readonly { key: string; label: string }[];
  /** Closed's outcomes; the first is the one a first close offers. */
  outcomes: readonly [PathOutcome, PathOutcome, ...PathOutcome[]];
}

/** The key of the Closed step. */
export const CLOSED = 'closed';

export const PATHS: Partial<Record<ObjectKey, PathDef>> = {
  opportunities: {
    field: 'stage',
    noun: 'stage',
    steps: [
      { key: 'qualify', label: 'Qualify' },
      { key: 'shape', label: 'Shape' },
      { key: 'propose', label: 'Propose' },
      { key: 'negotiate', label: 'Negotiate' },
      { key: 'commit', label: 'Commit' },
    ],
    outcomes: [
      { value: 'closed_won', label: 'Closed won', outcome: 'won' },
      { value: 'closed_lost', label: 'Closed lost', outcome: 'lost' },
    ],
  },
  leads: {
    field: 'status',
    noun: 'status',
    steps: [
      { key: 'new', label: 'New' },
      { key: 'contacted', label: 'Contacted' },
      { key: 'qualified', label: 'Qualified' },
    ],
    outcomes: [
      { value: 'converted', label: 'Converted', outcome: 'won' },
      { value: 'disqualified', label: 'Disqualified', outcome: 'lost' },
    ],
  },
  // A quote's status. Its primary quote moves the opportunity on (the server's QUOTE_STAGE_SYNC).
  quotes: {
    field: 'status',
    noun: 'status',
    steps: [
      { key: 'draft', label: 'Draft' },
      { key: 'presented', label: 'Presented' },
    ],
    outcomes: [
      { value: 'accepted', label: 'Accepted', outcome: 'won' },
      { value: 'declined', label: 'Declined', outcome: 'lost' },
      { value: 'expired', label: 'Expired', outcome: 'lost', offered: false },
    ],
  },
};

/** The outcomes a close offers to choose from. */
export function offeredOutcomes(def: PathDef): PathOutcome[] {
  return def.outcomes.filter((o) => o.offered !== false);
}

/** When the path field was set and by whom; by is null for a change made outside the app. */
export interface PathStamp {
  at: string;
  by: string | null;
}

/** One change of the path field, from the record's path_history. */
export interface PathChange extends PathStamp {
  value: string;
}

/** The record's path changes, oldest first, or none when it has no history. */
function changesOf(row: Record<string, unknown>): PathChange[] {
  return Array.isArray(row.path_history) ? (row.path_history as PathChange[]) : [];
}

/** "Oct 9 · Pat Example", or the date alone. */
export function stampText(stamp: PathStamp, now?: Date): string {
  const date = formatShortDate(stamp.at, now);
  return stamp.by ? `${date} · ${stamp.by}` : date;
}

/**
 * Each step's stamp: the first change to that step or any later one, Closed's
 * outcomes counting as the last step. A step skipped over takes the jump's
 * stamp, and a step keeps its stamp when the record later moves back.
 */
export function pathStamps(def: PathDef, changes: readonly PathChange[]): Map<string, PathStamp> {
  const keys = [...def.steps.map((s) => s.key), CLOSED];
  const rank = (value: string) =>
    def.outcomes.some((o) => o.value === value) ? keys.length - 1 : keys.indexOf(value);
  const stamps = new Map<string, PathStamp>();
  for (const change of changes) {
    const reached = rank(change.value);
    for (let i = 0; i <= reached; i++) {
      if (!stamps.has(keys[i]!)) stamps.set(keys[i]!, { at: change.at, by: change.by });
    }
  }
  return stamps;
}

/**
 * The record's Path: the open steps, then Closed named for its outcome once
 * closed. A step the record has reached, or skipped over, carries its stamp
 * as meta (see pathStamps).
 */
export function pathOf(
  def: PathDef,
  row: Record<string, unknown>,
  now?: Date,
): { stages: { key: string; label: string; meta?: string }[]; current: string; outcome?: 'won' | 'lost' } {
  const value = String(row[def.field] ?? '');
  const closed = def.outcomes.find((o) => o.value === value);
  const stamps = pathStamps(def, changesOf(row));
  const withMeta = (key: string, label: string) => {
    const stamp = stamps.get(key);
    return stamp ? { key, label, meta: stampText(stamp, now) } : { key, label };
  };
  const stages = [...def.steps.map((s) => withMeta(s.key, s.label)), withMeta(CLOSED, closed?.label ?? 'Closed')];
  return closed ? { stages, current: CLOSED, outcome: closed.outcome } : { stages, current: value };
}

/** The value a click on `key` proposes: the step itself, or for Closed the other outcome than the current one. */
export function proposedValue(def: PathDef, row: Record<string, unknown>, key: string): string {
  if (key !== CLOSED) return key;
  const value = String(row[def.field] ?? '');
  const [first, second] = offeredOutcomes(def);
  return value === first!.value ? second!.value : first!.value;
}
